<template>
  <div class="wg-table">
    <div v-if="!rows.length" class="wg-problem">No rows.</div>
    <table v-else>
      <thead>
        <tr><th v-for="col in shown" :key="col.field">{{ col.header }}</th></tr>
      </thead>
      <tbody>
        <tr v-for="(row, i) in limited" :key="i" :class="{ link: linkField && row[linkField] != null }" @click="open(row)">
          <td v-for="col in shown" :key="col.field" class="sensitive" :class="{ num: isNumber(row[col.field]) }">
            {{ format(row[col.field]) }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="rows.length" class="wg-foot">
      <span v-if="rows.length > limited.length">{{ limited.length }} of {{ rows.length }} rows</span>
      <button v-if="config.export !== false" type="button" class="wg-export" :disabled="exporting" @click="download">
        {{ exporting ? 'Exporting…' : `Export ${rows.length.toLocaleString()} rows to Excel` }}
      </button>
    </div>
  </div>
</template>

<script>
import { exportToExcel } from '@/utils/exportArraytoXlsx';

export default {
  name: 'TableWidget',
  props: {
    inputs: { type: Object, default: () => ({}) },
    config: { type: Object, default: () => ({}) },
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
  },
  data() {
    return { exporting: false };
  },
  computed: {
    rows() {
      return (this.inputs.rows || {}).rows || [];
    },
    columns() {
      return (this.inputs.rows || {}).columns || Object.keys(this.rows[0] || {});
    },
    shown() {
      const wanted = Array.isArray(this.config.columns) && this.config.columns.length ? this.config.columns : null;
      if (wanted) {
        return wanted.map((c) => (typeof c === 'string' ? { field: c, header: c } : { field: c.field, header: c.header || c.field }));
      }
      return this.columns.map((c) => ({ field: c, header: c }));
    },
    linkField() {
      return this.config.link_entity_field || '';
    },
    sorted() {
      const keys = (Array.isArray(this.config.sort) ? this.config.sort : []).filter((k) => k && k.field);
      if (!keys.length) return this.rows;
      const cmp = (a, b) => {
        if (a === b) return 0;
        if (a === null || a === undefined || a === '') return 1;
        if (b === null || b === undefined || b === '') return -1;
        const x = Number(a);
        const y = Number(b);
        if (Number.isFinite(x) && Number.isFinite(y)) return x - y;
        return String(a).localeCompare(String(b));
      };
      return [...this.rows].sort((r1, r2) => {
        for (const k of keys) {
          const blank = (v) => v === null || v === undefined || v === '';
          const c = cmp(r1[k.field], r2[k.field]);
          if (c) return blank(r1[k.field]) || blank(r2[k.field]) ? c : (k.dir === 'desc' ? -c : c);
        }
        return 0;
      });
    },
    limited() {
      const max = Number(this.config.max_rows) || 200;
      return this.sorted.slice(0, max);
    },
  },
  methods: {
    async download() {
      this.exporting = true;
      try {
        const rows = this.sorted.map((row) => Object.fromEntries(this.shown.map((c) => [c.header, row[c.field] ?? ''])));
        const name = String(this.config.title || 'table').replace(/[^A-Za-z0-9 _-]+/g, '').trim() || 'table';
        await exportToExcel(rows, `${name}.xlsx`, { sheetName: name.slice(0, 31) });
      } finally {
        this.exporting = false;
      }
    },
    open(row) {
      const id = this.linkField && row[this.linkField];
      if (id != null && id !== '') this.$router.push({ name: 'Entity', params: { entity_id: id } });
    },
    isNumber(v) {
      return typeof v === 'number';
    },
    format(v) {
      if (v === null || v === undefined) return '';
      if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
      return String(v);
    },
  },
};
</script>

<style scoped>
.wg-table { height: 100%; overflow: auto; font-size: 12.5px; }
table { border-collapse: collapse; width: 100%; }
th { position: sticky; top: 0; background: #faf9f6; text-align: left; font-weight: 600; color: #56534d; padding: 6px 10px; border-bottom: 1px solid #e6e4df; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.02em; }
td { padding: 5px 10px; border-bottom: 1px solid #f0efec; color: #1a1918; white-space: nowrap; }
td.num { text-align: right; font-variant-numeric: tabular-nums; }
tr.link { cursor: pointer; }
tr.link:hover td { background: #faf9f6; }
.wg-foot { display: flex; align-items: center; gap: 12px; padding: 6px 10px; font-size: 11px; color: #8b877f; }
.wg-export { margin-left: auto; font-size: 11.5px; font-weight: 600; color: #1a1918; border: 1px solid #d6d3cc; border-radius: 6px; padding: 3px 8px; background: #fff; }
.wg-export:hover { background: #faf9f6; }
.wg-export:disabled { opacity: 0.5; }
.wg-problem { padding: 16px; font-size: 12.5px; color: #8b877f; }
</style>
